import { db, storage, reference, getDownloadURL, uploadBytesResumable, deleteObject } from 'utils/firebase-config';
import { ref, onValue, set, update, remove, get, onChildAdded, onChildChanged, onChildRemoved, off, query, limitToLast, orderByKey, orderByChild, endBefore, endAt, serverTimestamp } from 'firebase/database';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import moment from 'moment';
import { getDistance, getMessageTime, normalizeDistance, sortRoomsByDistance } from 'helpers/utils';
import { api } from './conversation.service';
import { validateCurrentUserForFirebase } from 'utils/authUtils';
import { toast } from 'react-toastify';

/**
 * Middleware for Firebase write operations
 * Automatically validates current user and shows toast on error
 */
const withFirebaseUpdate = <T extends (...args: any[]) => Promise<any>>(
  fn: T
): T => {
  return (async (...args: any[]) => {
    try {
      // Validate current user before operation
      validateCurrentUserForFirebase();
      // Call the original function
      return await fn(...args);
    } catch (error: any) {
      // If validation fails, show toast and throw special error
      if (error.message && error.message.includes('INVALID_USER_DATA')) {
        toast.error('User data is missing or invalid. Please refresh and try again.');
        // Throw a special error that components can check for
        const userDataError: any = new Error('INVALID_USER_DATA');
        userDataError.code = 'INVALID_USER_DATA';
        throw userDataError;
      }
      // Re-throw other errors for components to handle
      throw error;
    }
  }) as T;
};
// import Interval from '../../utils/Interval';

// export const createRoom = async (room: any) => {
//   const createRoom: any = ref(db, `rooms/${room.roomId}`);
//   try {
//     await set(createRoom, {
//       roomId: room.roomId,
//       topic: room.topic,
//       host: {
//         id: room.host.pk,
//         name: room.host.name,
//         isSuperHost: room.host.is_super_host,
//         image: room.host.profile,
//       },
//       created_at: room.created_at,
//       isDeleted: false,
//       call_status: false,
//     });
//   } catch (err) {
//     console.log(err);
//   }
// };

export const createRoom = withFirebaseUpdate(async (room: any) => {
  const createRoom: any = ref(db, `rooms/${room.pk}`);
  const { host } = room;
  let timeStamp = new Date().toString()

  // Get location data for the room
  let roomCity = "Unknown";
  let roomCountry = "Unknown";
  
  if (room.latitude && room.longitude && room.latitude !== "null" && room.longitude !== "null") {
    try {
      // Import API function for geocoding
      const { api } = await import('./conversation.service');
      const locationResponse = await api.getLocationFromCoordinates({
        latitude: room.latitude.toString(),
        longitude: room.longitude.toString()
      });
      
      if (locationResponse && locationResponse.city && locationResponse.country) {
        roomCity = locationResponse.city;
        roomCountry = locationResponse.country;
      }
    } catch (error) {
      console.warn('Failed to get location for room:', error);
      // Continue with "Unknown" values
    }
  }

  try {
    await set(createRoom, {
      timeStamp: timeStamp,
      pk: room.pk,
      topic: room.topic,
      host: {
        pk: host.pk,
        name: host.name,
        email: host.email,
        role: host.role,
        profile: host.profile,
        latitude: host.latitude,
        longitude: host.longitude,
        silver_coin: host.silver_coin,
        gold_coin: host.gold_coin,
        is_super_host: host.is_super_host,
      },
      participants: room.participants,
      latitude: room.latitude === null ? "null" : room.latitude,
      longitude: room.longitude === null ? "null" : room.longitude,
      city: roomCity,        // NEW: Add city field
      country: roomCountry,  // NEW: Add country field
      created_at: room.created_at,
      is_deleted: room.is_deleted,
      status: room.status,
      
    });
  } catch (err) {
    console.log(err);
  }
});

export const updateRoomStatus = withFirebaseUpdate(async (data: any) => {
  const getRoom: any = ref(db, `rooms/${data.id}`);
  try {
    await update(getRoom, {
      status: data.status
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const updateMessageStatus = withFirebaseUpdate(async (room: any) => {
  const getRoom: any = ref(db, `chats/${room.roomId}/messages/${room.messageId}`)
  let pin = room.pin
  try {
    await update(getRoom, {
      pin: pin,
      timeStamp: room.timeStamp,
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const updateNewRoomStatus = withFirebaseUpdate(async (room: any) => {
  const getRoom: any = ref(db, `rooms/${room.room.pk}`);
  try {
    await update(getRoom, {
      activeRoom: room.activeRoom,
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const emailNotifications = withFirebaseUpdate(async (data: any) => {
  const createPinUsers: any = ref(db, `emailnotifications/${data.pk}`);
  try {
    await update(createPinUsers
      , {
        pk: data.pk,
        email: data.email,
        checked: data.checked
      }
    );
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const getEmailNotifications = () => {
  const getRoom: any = ref(db,`emailnotifications/${user.pk}`);
  let emailnotifications;
  onValue(getRoom, (querySnapshot: any) => {
    emailnotifications = querySnapshot.val()
  });
  return emailnotifications;
};

export const getRoomStatus = (roomId: any) => {
  
  // const getRoom: any = ref(db, `rooms/${roomId}`);
  // let aaaa = onValue(getRoom, (querySnapshot: any) => {
  //   console.log("~~~~~~~~~~~~~~~", querySnapshot);
    
  //   return querySnapshot.val()
  // });
  // console.log("0-0-0-0-0", aaaa);
  
  // const [room, setRoom] = useState<any>({});
  // useEffect(() => {
  //   onValue(getRoom, (querySnapshot: any) => {
  //     setRoom(querySnapshot.val());
  //   });
  // }, [roomId]);
  // return getRoom;
};

export const updateParticipantsInRoom = withFirebaseUpdate(async (room: any) => {
  const getRoom: any = ref(db, `rooms/${room.roomId}`);

  try {
    // Filter out undefined values created by previous deletions
    const validUsers = room.user.filter((user: any) => user !== undefined && user !== null);

    await update(getRoom, {
      participants: [
        ...validUsers
      ]
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const UpdateHostInRoom = withFirebaseUpdate(async (room: any) => {
  const getRoom: any = ref(db, `rooms/${room.roomId}`);
  try {
    await update(getRoom, {
      host: room.host
    });
  } catch (err) {
    console.log(err);
  }
});

export const updateLastSeenMessage = withFirebaseUpdate(async (roomId: number, userId: number) => {
  try {
    // Get the latest message ID for this room
    const messagesRef = ref(db, `chats/${roomId}/messages`);
    const messagesSnapshot = await get(messagesRef);
    
    if (messagesSnapshot.exists()) {
      const messages = Object.values(messagesSnapshot.val() || {});
      if (messages.length > 0) {
        // Sort messages by creation time to get the latest
        messages.sort((a: any, b: any) => {
          const aTime = a.timestamp || new Date(a.createdTime || 0).getTime();
          const bTime = b.timestamp || new Date(b.createdTime || 0).getTime();
          return bTime - aTime; // Descending order: latest first
        });

        const latestMessageId = (messages[0] as any)?.id;
        
        if (latestMessageId) {
          // Update the room with the latest message ID as lastSeenMessage
          const roomRef = ref(db, `rooms/${roomId}`);
          const roomSnapshot = await get(roomRef);
          
          if (roomSnapshot.exists()) {
            const roomData = roomSnapshot.val();
            if (roomData.host?.pk === userId) {
              // User is host, update host's 
              const updatedHost = {
                ...roomData.host,
                lastSeenMessage: latestMessageId
              };
              
              await update(roomRef, {
                host: updatedHost
              });
            } else if (roomData.participants) {
              // User is participant, update participant's lastSeenMessage
              const updatedParticipants = roomData.participants.map((participant: any) => {
                if (participant.pk === userId) {
                  return {
                    ...participant,
                    lastSeenMessage: latestMessageId
                  };
                }
                return participant;
              });
              
              await update(roomRef, {
                participants: updatedParticipants
              });
            }
          }
        }
      }
    }
  } catch (err) {
    console.log('Error updating lastSeenMessage:', err);
    throw err;
  }
});

export const deleteParticipants = withFirebaseUpdate(async (data: any) => {
  const participantRef: any = ref(db, `rooms/${data.roomId}/participants/${data.participantId}`);
  
  try {  
    await remove(participantRef);
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const deleteRoom = withFirebaseUpdate(async (room: any) => {
  const removeRoom: any = ref(db, `rooms/${room.roomId}`);
  try {
    await update(removeRoom, {
      is_deleted: true
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const ensureUserInWelcomeRoom = withFirebaseUpdate(async (user: any) => {
  const WELCOME_ROOM_ID = 981;

  // Filter out access, refresh fields from user
  const { access, refresh, ...userData } = user;
  
  try {
    const welcomeRoomRef = ref(db, `rooms/${WELCOME_ROOM_ID}`);
    const snapshot = await get(welcomeRoomRef);
    
    if (snapshot.exists()) {
      const roomData = snapshot.val();
      let participants = roomData.participants || [];
      participants = participants.filter((p: any) => p != null);

      // Check if user already joined the welcome room
      const joined = participants.some((participant: any) => participant.pk === userData.pk);

      if (!joined) {
        // Add user to welcome room participants
        await update(welcomeRoomRef, {
          participants: [...participants, userData]
        });
        
        console.log(`User ${userData.name} added to welcome room`);
      } else {
        console.log(`User ${userData.name} already in welcome room`);
      }
    }
  } catch (error) {
    console.error('Error ensuring user joined the welcome room:', error);
  }
});

export const useGetMessages = (pk: number) => {
  const [messagesMap, setMessagesMap] = useState<Map<string, any>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Pagination state for dynamic loading
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  
  // Use refs to store messages and prevent unnecessary re-renders
  const messagesMapRef = useRef<Map<string, any>>(new Map());
  const isUpdatingRef = useRef(false);
  const isInitializedRef = useRef(false);
  const listenersRef = useRef<{
    handleChildAdded: any;
    handleChildChanged: any;
    handleChildRemoved: any;
  } | null>(null);
  
  // Track oldest message for pagination
  const oldestMessageTimeRef = useRef<number | null>(null);
  
  // Removed unnecessary typing detection - not needed for message updates

  // Memoize the sorted messages array to prevent unnecessary re-renders
  const chat = useMemo(() => {
    const messages = Array.from(messagesMap.values());
    // Sort by timestamp (high-precision) first, fallback to createdTime for backward compatibility
    return messages.sort((a, b) => {
      const timeA = getMessageTime(a);
      const timeB = getMessageTime(b);
      return timeA - timeB; // Ascending order: oldest first
    });
  }, [messagesMap]); // Depend on the entire messagesMap for reactivity

  // Immediate update mechanism for real-time responsiveness
  const updateMessage = useCallback((messageId: string, message: any) => {
    // Always update the ref immediately for real-time access
    if (message && Object.keys(message).length > 0) {
      messagesMapRef.current.set(messageId, message);
    }
    
    // Update state immediately for real-time UI updates
    if (!isUpdatingRef.current && isInitializedRef.current) {
      isUpdatingRef.current = true;
      setMessagesMap(new Map(messagesMapRef.current));
      isUpdatingRef.current = false;
    }
  }, []);

  const removeMessage = useCallback((messageId: string) => {
    // Always update the ref immediately for real-time access
    messagesMapRef.current.delete(messageId);
    
    // Update state immediately for real-time UI updates
    if (!isUpdatingRef.current && isInitializedRef.current) {
      isUpdatingRef.current = true;
      setMessagesMap(new Map(messagesMapRef.current));
      isUpdatingRef.current = false;
    }
  }, []);

  // Function to load older messages
  const loadOlderMessages = useCallback(async () => {
    if (!pk || !hasMore || isLoadingMore) return;
    
    setIsLoadingMore(true);
    const messagesRef = ref(db, `chats/${pk}/messages`);
    
    try {
      let queryRef;
      
      if (oldestMessageTimeRef.current) {
        queryRef = query(
          messagesRef, 
          orderByChild('timestamp'), 
          endBefore(oldestMessageTimeRef.current), 
          limitToLast(10)
        );
      } else {
        queryRef = query(
          messagesRef, 
          orderByChild('timestamp'), 
          limitToLast(10)
        );
      }
      
      const snapshot = await get(queryRef);
      
      // FIXED: Use the current state instead of the ref
      const newMessagesMap = new Map(messagesMap); // Use messagesMap state, not ref
      
      let messageCount = 0;
      let oldestTime: number | null = null;
      
      snapshot.forEach((child) => {
        const message = child.val();
        if (message && Object.keys(message).length > 0) {
          // Only add if not already exists
          if (!newMessagesMap.has(message.id)) {
            newMessagesMap.set(message.id, message);
            messageCount++;
            
            // Use timestamp for new messages, fallback to createdTime for compatibility
            const messageTime = message.timestamp || (message.createdTime ? new Date(message.createdTime).getTime() : null);
            if (messageTime && (!oldestTime || messageTime < oldestTime)) {
              oldestTime = messageTime;
            }
          }
        }
      });
      
      if (oldestTime) {
        oldestMessageTimeRef.current = oldestTime;
      }
      
      // Update both ref and state
      messagesMapRef.current = newMessagesMap;
      setMessagesMap(new Map(newMessagesMap));
      setHasMore(messageCount === 10);     
    } catch (error) {
      console.error("Error loading older messages:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [pk, hasMore, isLoadingMore, messagesMap]); // Add messagesMap to dependencies

  useEffect(() => {
    if (!pk) return;

    const messagesRef = ref(db, `chats/${pk}/messages`);
    setIsLoading(true);

    // Reset pagination state when changing rooms
    setHasMore(true);
    setIsLoadingMore(false);
    oldestMessageTimeRef.current = null;
    
    // Reset initialization flag when changing rooms
    isInitializedRef.current = false;

    // Clean up any existing listeners for this specific room
    if (listenersRef.current) {
      off(messagesRef, 'child_added', listenersRef.current.handleChildAdded);
      off(messagesRef, 'child_changed', listenersRef.current.handleChildChanged);
      off(messagesRef, 'child_removed', listenersRef.current.handleChildRemoved);
    }

    // Initial load - use Firebase query to get only limited amount of last messages
    const initialLoadLimit = 10; // Define a limit for the initial load
    // Use 24-hour buffer to handle server timestamp differences and prevent caching
    const bufferMs = 24 * 60 * 60 * 1000; // 24 hours buffer
    const initialQuery = query(messagesRef, orderByChild('timestamp'), endAt(Date.now() + bufferMs), limitToLast(initialLoadLimit));
    
    // Store the limited query reference for listeners
    const limitedMessagesRef = initialQuery;
    
    get(initialQuery).then((snapshot) => {
      const newMessagesMap = new Map();
      let messageCount = 0;
      
      // Firebase now only returns limited amount of messages
      snapshot.forEach((child) => {
        const message = child.val();
        if (message && Object.keys(message).length > 0) {
          newMessagesMap.set(message.id, message);
          messageCount++;
        }
      });
      
      // Update both ref and state
      messagesMapRef.current = newMessagesMap;
      setMessagesMap(newMessagesMap);
      setIsLoading(false);
      
      setHasMore(messageCount === initialLoadLimit);
      
      // Track the oldest message time for pagination
      if (messageCount > 0) {
        let oldestTime: number | null = null;
        snapshot.forEach((child) => {
          const message = child.val();
          // Use timestamp for new messages, fallback to createdTime for compatibility
          const messageTime = message.timestamp || (message.createdTime ? new Date(message.createdTime).getTime() : null);
          if (message && messageTime && (!oldestTime || messageTime < oldestTime)) {
            oldestTime = messageTime;
          }
        });
        if (oldestTime) {
          oldestMessageTimeRef.current = oldestTime;
        }
      }
      
      // Mark as initialized after initial load
      isInitializedRef.current = true;
      
      // Only set up listeners AFTER initial load to prevent duplicates
      
      // Listen for new messages - attach to limited query, not full messagesRef
      const handleChildAdded = onChildAdded(limitedMessagesRef, (snapshot) => {
        const message = snapshot.val();
        if (message && Object.keys(message).length > 0) {
          updateMessage(message.id, message);
        }
      });

      // Listen for message changes/updates - attach to limited query
      const handleChildChanged = onChildChanged(limitedMessagesRef, (snapshot) => {
        const updatedMessage = snapshot.val();
        if (updatedMessage && Object.keys(updatedMessage).length > 0) {
          updateMessage(updatedMessage.id, updatedMessage);
        }
      });

      // Listen for message deletions - attach to limited query
      const handleChildRemoved = onChildRemoved(limitedMessagesRef, (snapshot) => {
        const deletedMessage = snapshot.val();
        if (deletedMessage) {
          removeMessage(deletedMessage.id);
        }
      });
      
      // Store listeners for cleanup
      const listeners = {
        handleChildAdded,
        handleChildChanged,
        handleChildRemoved
      };
      
      // Store listeners in a ref for cleanup
      listenersRef.current = listeners;
      
    }).catch((error) => {
      console.error("Error loading messages:", error);
      setIsLoading(false);
      isInitializedRef.current = true; // Mark as initialized even on error
    });

    // Listeners are now set up AFTER initial load to prevent duplicates

    // Cleanup listeners on unmount
    return () => {
      // Clean up stored listeners if they exist
      if (listenersRef.current) {
        off(messagesRef, 'child_added', listenersRef.current.handleChildAdded);
        off(messagesRef, 'child_changed', listenersRef.current.handleChildChanged);
        off(messagesRef, 'child_removed', listenersRef.current.handleChildRemoved);
      }
      
      // No pending updates to clear - immediate updates
      
      // Reset initialization flag
      isInitializedRef.current = false;
      
      // Clear listeners ref
      listenersRef.current = null;
    };
  }, [pk]);

  return { 
    chat, 
    isLoading,
    // Pagination properties for dynamic loading
    hasMore,
    isLoadingMore,
    loadOlderMessages,
    isInitialized: isInitializedRef.current
  };
};

// Custom hook for thread messages
export const useGetThreadMessages = (roomId: number, parentMessageId: string) => {
  const [threads, setThreads] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isInitializedRef = useRef(false);
  const listenersRef = useRef<any>(null);

  // Memoize the sorted threads array
  const sortedThreads = useMemo(() => {
    return threads.sort((a, b) => {
      const timeA = getMessageTime(a);
      const timeB = getMessageTime(b);
      return timeA - timeB; // Ascending order: oldest first
    });
  }, [threads]);

  useEffect(() => {
    if (!roomId || !parentMessageId) return;

    const threadsRef = ref(db, `chats/${roomId}/messages/${parentMessageId}/threads`);
    setIsLoading(true);
    isInitializedRef.current = false;

    // Clean up any existing listener
    if (listenersRef.current) {
      off(threadsRef, 'value', listenersRef.current);
    }

    // Set up real-time listener
    const handleValueChange = onValue(threadsRef, (snapshot) => {
      const threadsData = snapshot.val();
      if (threadsData) {
        const threadsArray = Array.isArray(threadsData) ? threadsData : Object.values(threadsData);
        setThreads(threadsArray);
      } else {
        setThreads([]);
      }
      setIsLoading(false);
      isInitializedRef.current = true;
    });

    // Store listener for cleanup
    listenersRef.current = handleValueChange;

    // Cleanup listener on unmount
    return () => {
      if (listenersRef.current) {
        off(threadsRef, 'value', listenersRef.current);
      }
      listenersRef.current = null;
      isInitializedRef.current = false;
    };
  }, [roomId, parentMessageId]);

  return { 
    threads: sortedThreads, 
    isLoading,
    isInitialized: isInitializedRef.current
  };
};

const user = JSON.parse(localStorage.getItem("user") as string);
export const useGetRooms = () => {
  const [rooms, setRooms] = useState<any>([]);
  
  useEffect(() => {
    const roomsRef = ref(db, "rooms");
    
    const unsubscribe = onValue(roomsRef, (querySnapshot: any) => {
      let allRooms: any = [];
      querySnapshot.forEach((doc: any) => {
        const data = doc.val();
        if (data.is_deleted !== true) {
          allRooms.push({
            ...data,
            distance: getDistance(
              user?.latitude,
              user?.longitude,
              data.host?.latitude,
              data.host?.longitude
            ),
          });
        }
      });
      allRooms = sortRoomsByDistance(allRooms);
      setRooms(allRooms);
    });

    // Cleanup function to unsubscribe when component unmounts
    return () => {
      unsubscribe();
    };
  }, []);
  
  return rooms;
};

export const sendFCMMessage = withFirebaseUpdate(async (message: any) => {
  const roomId = message.roomId;
  const messages: any = ref(db, `chats/${roomId}/messages/${message.id}`);
  try {
    await set(messages, {
      id: message.id,
      sender: message.sender,
      receiver: message.receiver,
      message: message.message,
      time: message.time,
      is_delete: message.is_delete,
      attachments: message.attachments,
      threads: message.threads,
      createdTime: moment().format(),  // Client time for UI display
      timestamp: serverTimestamp()     // Server timestamp for consistent ordering
    });
  } catch (err) {
    console.log(err);
  }
});

export const sendThreadMessage = withFirebaseUpdate(async (message: any) => {
  const roomId = message.roomId;
  
  try {
    // Use get() for one-time read instead of onValue listener
    const messagesRef = ref(db, `chats/${roomId}/messages`);
    const messagesSnapshot = await get(messagesRef);
    
    // Find the parent message
    let findMsg: any = {};
    messagesSnapshot.forEach((doc: any) => {
      if (doc.val().id === message.parentId) {
        findMsg = doc.val();
      }
    });
    
    // Check if parent message exists
    if (!findMsg || Object.keys(findMsg).length === 0) {
      throw new Error('Parent message not found');
    }
    
    const isThreadsExist = findMsg.hasOwnProperty('threads');
    let length = 0;
    
    if (isThreadsExist) {
      // Use get() for one-time read of threads
      const threadsRef = ref(db, `chats/${roomId}/messages/${message.parentId}/threads`);
      const threadsSnapshot = await get(threadsRef);
      const obj = threadsSnapshot.val();
      
      // Handle both array and object formats
      if (Array.isArray(obj)) {
        length = obj.length;
      } else if (obj && typeof obj === 'object') {
        length = Object.keys(obj).length;
      } else {
        length = 0;
      }
    }
    
    if (length === 0) {
      // Create threads array with first message
      const messages: any = ref(db, `chats/${roomId}/messages/${message.parentId}`);
      await update(messages, {
        threads: [
          {
            id: message.id,
            sender: message.sender,
            receiver: message.receiver,
            message: message.message,
            time: message.time,
            is_delete: message.is_delete,
            attachments: message.attachments,
            createdTime: moment().format(),
            timestamp: serverTimestamp()
          }
        ]
      });
    } else {
      // Append to existing threads
      // Use push() for arrays or set with generated key for objects
      const threadsRef = ref(db, `chats/${roomId}/messages/${message.parentId}/threads`);
      const threadsSnapshot = await get(threadsRef);
      const threadsData = threadsSnapshot.val();
      
      const newThread = {
        id: message.id,
        sender: message.sender,
        receiver: message.receiver,
        message: message.message,
        time: message.time,
        is_delete: message.is_delete,
        attachments: message.attachments,
        createdTime: moment().format(),
        timestamp: serverTimestamp()
      };
      
      if (Array.isArray(threadsData)) {
        // If threads is an array, append to it
        await update(threadsRef, {
          [length]: newThread
        });
      } else {
        // If threads is an object, add with a new key
        const newKey = length.toString();
        await update(threadsRef, {
          [newKey]: newThread
        });
      }
    }
  } catch (err) {
    console.error('Error sending thread message:', err);
    throw err; // Re-throw to allow component error handling
  }
});

export const deleteMessage = withFirebaseUpdate(async (path: any) => {
  const message: any = ref(db, `chats/${path}`);
  try {
    await remove(message);
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const deleteImage = async (path: any) => {
  const desertRef = reference(storage, `${path}`);
  deleteObject(desertRef).then(() => {
    // File deleted successfully
  }).catch((error) => {
    // Uh-oh, an error occurred!
  });
};

export const deleteThreadMessage = withFirebaseUpdate(async (roomId: string, parentMessageId: string, threadIndex: number) => {
  try {
    // Get current threads array
    const threadsRef = ref(db, `chats/${roomId}/messages/${parentMessageId}/threads`);
    const snapshot = await get(threadsRef);
    const threads = snapshot.val() || [];
    
    // Remove the thread at the specified index
    const updatedThreads = threads.filter((_: any, index: number) => index !== threadIndex);
    
    // Update the threads array
    await set(threadsRef, updatedThreads);
    console.log('Thread message deleted successfully');
  } catch (error) {
    console.error('Error deleting thread message:', error);
    throw error;
  }
});

export const getImages = async (roomId: string, attachmentId: string) => {
  const images: any = [];
  try {
    // Extract the actual room ID from the attachment ID
    // Attachment ID format: "778-dR2KdtpbKSEjxKf" where 778 is the room ID
    const actualRoomId = attachmentId.split('-')[0];
    
    const url1 = await getDownloadURL(reference(storage, `/${actualRoomId}/${attachmentId}`));
    if (url1) images[0] = url1;
    return images;
  } catch (err: any) {
    // Silently handle missing attachment files to prevent 404 errors in console
    if (err.code === 'storage/object-not-found') {
      console.warn(`Attachment file not found: ${attachmentId}`);
      return images;
    }
    console.error('Error getting image:', err);
  }
  return images;
};

export const uploadImage = async (message: any, file: any) => {
  try {
    return await uploadBytesResumable(reference(storage, `/${message.roomId}/${file.id}`), file.file);
  } catch (err) {}
  return '';
};

export const updateCallStatus = withFirebaseUpdate(async (room: any) => {
  const getRoom: any = ref(db, `rooms/${room.roomId}`);
  try {
    await update(getRoom, {
      call_status: room.status
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const useGetCallStatus = (roomId: any) => {
  const [room, setRoom] = useState<any>({});
  
  useEffect(() => {
    if (!roomId) return;
    
    const getRoom: any = ref(db, `rooms/${roomId}`);
    
    // Set up the listener
    const unsubscribe = onValue(getRoom, (querySnapshot: any) => {
      setRoom(querySnapshot.val());
    });
    
    // Cleanup listener on unmount or when roomId changes
    return () => {
      unsubscribe();
    };
  }, [roomId]);
  
  return room;
};

export const sendJoinRequest = withFirebaseUpdate(async (request: any) => {
  const roomId = request.roomId;
  
  try {    
    const requestsRef = ref(db, `requests/${roomId}/requests`);
    
    // Check if there's already a pending request from this user
    const snapshot = await get(requestsRef);
    const existingRequests = snapshot.val() || {};
    
    // Check if user already has a pending request
    const userHasPendingRequest = Object.values(existingRequests).some((req: any) => 
      req.user?.pk === request.user.pk
    );
    
    if (userHasPendingRequest) {
      // User already has a pending request, throw specific error
      const error: any = new Error('DUPLICATE_REQUEST');
      error.code = 'DUPLICATE_REQUEST';
      throw error;
    }
    
    // No pending request found, send new request
    const newRequestRef = ref(db, `requests/${roomId}/requests/${request.id}`);
    await set(newRequestRef, {
      id: request.id,
      user: request.user,
      time: request.time,
    });
    
    // Call the backend API to send email notification
    await api.sendJoinRequestEmail({ room_id: String(roomId) });
  } catch (err) {
    console.log('Error sending join request:', err);
    throw err; // Re-throw to handle in UI
  }
});

export const deleteRequest = withFirebaseUpdate(async (path: any) => {
  const request: any = ref(db, `requests/${path}`);
  try {
    await set(request, {});
  } catch (err) {
    console.log(err);
    throw err;
  }
});


// Function to check if room has location data
export const hasLocationData = (room: any) => {
  return room && room.city && room.country && 
    room.city !== "Unknown" && room.country !== "Unknown";
};

// Function to get room location with fallback
export const getRoomLocation = (room: any) => {
  if (hasLocationData(room)) {
    return {
      city: room.city,
      country: room.country
    };
  }
  return {
    city: "Unknown",
    country: "Unknown"
  };
};


